import type { Entity } from '@backstage/catalog-model';
import {
  filterValueMatches,
  getValueByPath,
  type CheckDefinition,
  type CheckFilter,
} from '@internal/backstage-plugin-pulse-common';

export type CheckApplicability = 'applicable' | 'excluded';

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return [value];
};

const ruleEntryMatches = (
  entity: Entity,
  key: string,
  expectedValue: unknown,
): boolean => {
  let actualValue: unknown;

  if (key === 'kind') {
    actualValue = entity.kind;
  } else if (key === 'namespace') {
    actualValue = entity.metadata.namespace;
  } else if (key === 'name') {
    actualValue = entity.metadata.name;
  } else {
    actualValue = getValueByPath(entity, key);
  }

  const actualValues = toArray(actualValue);
  const expectedValues = toArray(expectedValue);

  return expectedValues.some(expected =>
    actualValues.some(actual => filterValueMatches(actual, expected)),
  );
};

const ruleMatches = (entity: Entity, rule: CheckFilter): boolean => {
  return Object.entries(rule).every(([key, value]) =>
    ruleEntryMatches(entity, key, value),
  );
};

const matchesAnyRule = (entity: Entity, rules?: CheckFilter[]): boolean => {
  if (!rules?.length) {
    return false;
  }

  return rules.some(rule => ruleMatches(entity, rule));
};

export const getCheckApplicability = (
  check: Pick<CheckDefinition, 'filter' | 'exclude'> | undefined,
  entity: Entity,
): CheckApplicability => {
  if (!check) {
    return 'excluded';
  }

  const includedByFilter = check.filter?.length
    ? matchesAnyRule(entity, check.filter)
    : true;

  if (includedByFilter === false) {
    return 'excluded';
  }

  if (matchesAnyRule(entity, check.exclude)) {
    return 'excluded';
  }

  return 'applicable';
};
